import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface MyConnectionProfile {
  id: string;
  full_name: string;
  username: string;
  profile_picture_url?: string;
  belt_rank?: string;
  academy?: string;
}

const CONNECTIONS_PER_PAGE = 10;

export function useMyConnections() {
  const { user } = useAuth();

  return useInfiniteQuery({
    queryKey: ['my-connections'],
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      if (!user) return [];

      // Fetch the user_ids of the connections using the function
      const { data: connectionIds, error } = await supabase.rpc('get_my_connections');

      if (error) throw error;

      const ids = (connectionIds || []).map((r: any) => r.user_id).filter(Boolean);
      
      if (ids.length === 0) return [];

      // Apply pagination to the results
      const startIndex = pageParam * CONNECTIONS_PER_PAGE;
      const endIndex = startIndex + CONNECTIONS_PER_PAGE;
      const paginatedIds = ids.slice(startIndex, endIndex);

      if (paginatedIds.length === 0) return [];

      // Fetch the complete profiles of the connections, including main academy
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, full_name, username, belt_rank, main_academy:main_academy_id(name)')
        .in('id', paginatedIds);

      if (profileError) throw profileError;

      const connectionsWithAcademy = (profiles || []).map((p: any) => ({
        ...p,
        academy: p.main_academy ? p.main_academy.name : undefined,
      }));

      return connectionsWithAcademy;
    },
    getNextPageParam: (lastPage: MyConnectionProfile[], allPages: MyConnectionProfile[][]) => {
      return lastPage.length === CONNECTIONS_PER_PAGE ? allPages.length : undefined;
    },
    enabled: !!user,
  });
} 